/**
 * Community Group Create Page
 * /community/groups/new - 모임 만들기 페이지
 */

import { useState } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Upload, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { buildLoginPath } from "@/lib/auth-navigation";
import { useQuery, useMutation } from "@tanstack/react-query";
import { australianStates, getSuburbsByState, type SuburbInfo } from "@shared/suburbs";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  title: z.string().min(3, "모임명은 최소 3자 이상이어야 합니다").max(100),
  description: z.string().min(10, "설명은 최소 10자 이상이어야 합니다").max(2000),
  categoryId: z.string().min(1, "카테고리를 선택해주세요"),
  city: z.string().min(1, "도시를 입력해주세요"),
  state: z.string().min(2, "주를 선택해주세요"),
  suburb: z.string().optional(),
  isJoinApprovalRequired: z.boolean().default(false),
  maxMembers: z.number().int().positive().optional(),
  coverImageUrl: z.string().url().optional().or(z.literal("")),
});

type FormData = z.infer<typeof formSchema>;

interface GroupCategory {
  id: number;
  slug: string;
  name: string;
  name_ko: string;
  icon: string;
}

export default function GroupCreatePage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      isJoinApprovalRequired: false,
    },
  });

  // watch는 useForm 이후에 사용해야 함
  const selectedState = watch("state");
  const availableCities = selectedState ? getSuburbsByState(selectedState) : [];

  // 카테고리 목록 조회
  const { data: categoriesData } = useQuery<{ categories: GroupCategory[] }>({
    queryKey: ["/api/community/categories"],
    queryFn: async () => {
      const response = await fetch("/api/community/categories");
      if (!response.ok) throw new Error("Failed to fetch categories");
      return response.json();
    },
  });

  const categories = categoriesData?.categories || [];

  // 모임 생성
  const createGroupMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!user) throw new Error("로그인이 필요합니다");

      const token = await user.getIdToken();
      const requestBody = {
        ...data,
        categoryId: parseInt(data.categoryId),
        maxMembers: data.maxMembers || null,
      };

      console.log("📤 Sending group creation request:", requestBody);

      const response = await fetch("/api/community/groups", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log("📥 Response status:", response.status, response.statusText);

      if (!response.ok) {
        let errorMessage = "모임 생성에 실패했습니다";
        try {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
          console.error("❌ API error response:", error);
        } catch (e) {
          const errorText = await response.text();
          console.error("❌ API error text:", errorText);
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("✅ Group created successfully:", result);
      return result;
    },
    onSuccess: (data) => {
      toast({
        title: "모임이 생성되었습니다",
        description: "모임 상세 페이지로 이동합니다.",
      });
      navigate(`/community/groups/${data.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 이미지 업로드
  const uploadImageMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error("로그인이 필요합니다");

      const token = await user.getIdToken();

      // 1. 업로드 키 받기
      const keyResponse = await fetch("/api/community/upload/presigned-url", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          folder: "community/groups",
        }),
      });

      if (!keyResponse.ok) throw new Error("업로드 키 생성 실패");
      const { key, uploadEndpoint } = await keyResponse.json();

      // 2. 파일 업로드
      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await fetch(uploadEndpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) throw new Error("파일 업로드 실패");
      const uploadData = await uploadResponse.json();

      return uploadData.url;
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      console.log("📝 Submitting group creation form...", data);

      // 도시 이름 정규화 (한글 입력 시 영어로 변환)
      if (data.city) {
        const cityInfo = availableCities.find(
          (c) => c.name === data.city || c.nameKo === data.city || c.id === data.city.toLowerCase()
        );
        if (cityInfo) {
          data.city = cityInfo.name; // 영어 이름으로 저장
          console.log("📍 City normalized:", data.city);
        }
      }

      // 이미지가 있으면 먼저 업로드
      if (coverImageFile) {
        console.log("📤 Uploading cover image...");
        const imageUrl = await uploadImageMutation.mutateAsync(coverImageFile);
        data.coverImageUrl = imageUrl;
        console.log("✅ Image uploaded:", imageUrl);
      }

      console.log("🚀 Creating group with data:", {
        ...data,
        categoryId: parseInt(data.categoryId),
        maxMembers: data.maxMembers || null,
      });

      await createGroupMutation.mutateAsync(data);
    } catch (error: any) {
      console.error("❌ Error creating group:", error);
      toast({
        title: "오류",
        description: error?.message || "모임 생성 중 오류가 발생했습니다",
        variant: "destructive",
      });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setCoverImageFile(null);
    setCoverImagePreview(null);
    setValue("coverImageUrl", "");
  };

  if (!user) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <h3 className="text-xl font-semibold mb-2">로그인이 필요합니다</h3>
          <p className="text-muted-foreground mb-4">모임을 만들려면 로그인해주세요.</p>
          <Button asChild>
            <a href={buildLoginPath()}>로그인하기</a>
          </Button>
        </Card>
      </PageContainer>
    );
  }

  return (
    <>
      <Seo
        title="모임 만들기 — OZKNEWS"
        description="새로운 교민모임을 만들어보세요."
        path="/community/groups/new"
      />

      <div className="min-h-screen bg-background">
        <PageContainer size="default">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/community/groups")}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">모임 만들기</h1>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-2">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center flex-1 min-w-0">
                  <div
                    className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 text-xs sm:text-base flex-shrink-0 ${
                      step >= s
                        ? "bg-[#c066ac] border-[#c066ac] text-white"
                        : "border-muted text-muted-foreground"
                    }`}
                  >
                    {s}
                  </div>
                  {s < 4 && (
                    <div
                      className={`flex-1 h-1 mx-1 sm:mx-2 min-w-[20px] ${
                        step > s ? "bg-[#c066ac]" : "bg-muted"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Step 1: Basic Info */}
              {step === 1 && (
                <Card>
                  <CardHeader>
                    <CardTitle>기본 정보</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="title">모임명 *</Label>
                      <Input
                        id="title"
                        {...register("title")}
                        placeholder="예: 시드니 한식 쿠킹클럽"
                        className="mt-1"
                      />
                      {errors.title && (
                        <p className="text-sm text-destructive mt-1">{errors.title.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="description">모임 소개 *</Label>
                      <Textarea
                        id="description"
                        {...register("description")}
                        placeholder="모임에 대해 자세히 설명해주세요..."
                        className="mt-1 min-h-[120px]"
                      />
                      {errors.description && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.description.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="categoryId">카테고리 *</Label>
                      <Select
                        onValueChange={(value) => setValue("categoryId", value)}
                        defaultValue={watch("categoryId")}
                      >
                        <SelectTrigger id="categoryId" className="mt-1">
                          <SelectValue placeholder="카테고리 선택" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={String(category.id)}>
                              {category.name_ko || category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.categoryId && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.categoryId.message}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate("/community/groups")}
                        className="w-full sm:w-auto"
                      >
                        취소
                      </Button>
                      <Button
                        type="button"
                        onClick={async () => {
                          const isValid = await trigger(["title", "description", "categoryId"]);
                          if (isValid) {
                            setStep(2);
                          }
                        }}
                        className="w-full sm:w-auto"
                      >
                        다음
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Step 2: Location & Settings */}
              {step === 2 && (
                <Card>
                  <CardHeader>
                    <CardTitle>지역 및 설정</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="state">주 *</Label>
                      <Select
                        onValueChange={(value) => setValue("state", value)}
                        defaultValue={watch("state")}
                      >
                        <SelectTrigger id="state" className="mt-1">
                          <SelectValue placeholder="주 선택" />
                        </SelectTrigger>
                        <SelectContent>
                          {australianStates.map((state) => (
                            <SelectItem key={state.id} value={state.id}>
                              {state.nameKo}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.state && (
                        <p className="text-sm text-destructive mt-1">{errors.state.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="city">도시 *</Label>
                      {availableCities.length > 0 ? (
                        <Select
                          onValueChange={(value) => {
                            const selectedCity = availableCities.find((c) => c.id === value);
                            if (selectedCity) {
                              setValue("city", selectedCity.name); // 영어 이름으로 저장
                            }
                          }}
                          defaultValue={watch("city") ? availableCities.find((c) => c.name === watch("city"))?.id : undefined}
                        >
                          <SelectTrigger id="city" className="mt-1">
                            <SelectValue placeholder="도시 선택" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableCities.map((city) => (
                              <SelectItem key={city.id} value={city.id}>
                                {city.nameKo ? `${city.nameKo} (${city.name})` : city.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id="city"
                          {...register("city")}
                          placeholder="주를 먼저 선택해주세요"
                          className="mt-1"
                          disabled={!selectedState}
                        />
                      )}
                      {errors.city && (
                        <p className="text-sm text-destructive mt-1">{errors.city.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="suburb">지역 (선택사항)</Label>
                      <Input
                        id="suburb"
                        {...register("suburb")}
                        placeholder="예: Eastwood, Chatswood"
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        구체적인 지역명을 입력하세요 (예: Eastwood, Chatswood, Parramatta)
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="maxMembers">최대 인원 (선택사항)</Label>
                      <Input
                        id="maxMembers"
                        type="number"
                        {...register("maxMembers", { valueAsNumber: true })}
                        placeholder="제한없음"
                        className="mt-1"
                        min="1"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isJoinApprovalRequired"
                        {...register("isJoinApprovalRequired")}
                        className="rounded"
                      />
                      <Label htmlFor="isJoinApprovalRequired" className="cursor-pointer">
                        가입 승인 필요
                      </Label>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setStep(1)}
                        className="w-full sm:w-auto"
                      >
                        이전
                      </Button>
                      <Button
                        type="button"
                        onClick={async () => {
                          const isValid = await trigger(["state", "city"]);
                          if (isValid) {
                            setStep(3);
                          }
                        }}
                        className="w-full sm:w-auto"
                      >
                        다음
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Step 3: Cover Image */}
              {step === 3 && (
                <Card>
                  <CardHeader>
                    <CardTitle>대표 이미지</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {coverImagePreview ? (
                      <div className="relative">
                        <img
                          src={coverImagePreview}
                          alt="Cover preview"
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2"
                          onClick={removeImage}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-muted rounded-lg p-12 text-center">
                        <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <Label htmlFor="coverImage" className="cursor-pointer">
                          <Button type="button" variant="outline" asChild>
                            <span>이미지 업로드</span>
                          </Button>
                        </Label>
                        <input
                          id="coverImage"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                        <p className="text-sm text-muted-foreground mt-2">
                          JPG, PNG, WEBP (최대 5MB)
                        </p>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setStep(2)}
                        className="w-full sm:w-auto"
                      >
                        이전
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setStep(4)}
                        className="w-full sm:w-auto"
                      >
                        다음
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Step 4: Preview & Submit */}
              {step === 4 && (
                <Card>
                  <CardHeader>
                    <CardTitle>미리보기</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-lg">{watch("title")}</h3>
                        <p className="text-sm text-muted-foreground">{watch("description")}</p>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>
                          지역: {watch("city")}, {watch("state")}
                        </p>
                        <p>카테고리: {categories.find((c) => String(c.id) === watch("categoryId"))?.name_ko}</p>
                        {watch("maxMembers") && !isNaN(Number(watch("maxMembers"))) && (
                          <p>최대 인원: {Number(watch("maxMembers"))}명</p>
                        )}
                        <p>가입 승인: {watch("isJoinApprovalRequired") ? "필요" : "자동"}</p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setStep(3)}
                        className="w-full sm:w-auto"
                      >
                        이전
                      </Button>
                      <Button
                        type="button"
                        disabled={createGroupMutation.isPending || uploadImageMutation.isPending}
                        className="bg-[#c066ac] hover:bg-[#b055a0] w-full sm:w-auto"
                        onClick={handleSubmit(onSubmit)}
                      >
                        {createGroupMutation.isPending || uploadImageMutation.isPending
                          ? "생성 중..."
                          : "모임 만들기"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </form>
          </div>
        </PageContainer>
      </div>
    </>
  );
}
